(function () {
  'use strict';

  var MAX_ORBS = 5;
  var thumbObserver = null;
  var loaderMotionObserver = null;

  function getOrbWindow(current, total) {
    if (total <= MAX_ORBS) {
      return { start: 0, count: total };
    }
    var start = current - Math.floor(MAX_ORBS / 2);
    if (start < 0) start = 0;
    if (start > total - MAX_ORBS) start = total - MAX_ORBS;
    return { start: start, count: MAX_ORBS };
  }

  function hydrateImage(img) {
    if (!img || img.dataset.ttHydrated === '1') return;
    var src = img.getAttribute('data-tt-src');
    if (!src && img.getAttribute('src')) {
      img.dataset.ttHydrated = '1';
      return;
    }
    if (!src) return;

    img.dataset.ttHydrated = '1';
    var srcset = img.getAttribute('data-tt-srcset');
    var sizes = img.getAttribute('data-tt-sizes');
    if (srcset) img.setAttribute('srcset', srcset);
    if (sizes) img.setAttribute('sizes', sizes);
    img.setAttribute('src', src);
    img.classList.remove('is-deferred');
    img.removeAttribute('data-tt-src');
    img.removeAttribute('data-tt-srcset');
    img.removeAttribute('data-tt-sizes');
  }

  function markImageLoaded(img) {
    if (!img) return;
    img.classList.add('is-loaded');
    img.classList.remove('is-deferred');
    var media = img.closest('[data-tt-thumb-carousel]');
    if (!media) return;

    var priority = media.querySelector('[data-tt-thumb-priority="1"]');
    if (!priority || priority === img || priority.classList.contains('is-loaded')) {
      media.classList.add('is-thumb-ready');
      media.classList.remove('is-thumb-far');
      if (loaderMotionObserver) loaderMotionObserver.unobserve(media);
    }
  }

  // Shimmer + orbit loaders are infinite animations; only run them on cards near the screen.
  function watchLoaderMotion(root) {
    if (!('IntersectionObserver' in window) || root.classList.contains('is-thumb-ready')) return;
    if (!loaderMotionObserver) {
      loaderMotionObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            entry.target.classList.toggle('is-thumb-far', !entry.isIntersecting);
          });
        },
        { rootMargin: '200px 0px' }
      );
    }
    loaderMotionObserver.observe(root);
  }

  function bindImageLoad(img) {
    if (!img || img.dataset.ttLoadBound === '1') return;
    img.dataset.ttLoadBound = '1';

    if (img.complete && img.naturalWidth > 0) {
      markImageLoaded(img);
      return;
    }

    img.addEventListener(
      'load',
      function () {
        markImageLoaded(img);
      },
      { once: true }
    );
    img.addEventListener(
      'error',
      function () {
        markImageLoaded(img);
      },
      { once: true }
    );
  }

  function ensureSlideImage(root, index) {
    var slides = root.querySelectorAll('[data-tt-thumb-slide]');
    var slide = slides[index];
    if (!slide) return;
    var img = slide.querySelector('[data-tt-thumb-img]');
    if (!img) return;
    hydrateImage(img);
    bindImageLoad(img);
  }

  function initThumbLoading(root) {
    if (!root || root.dataset.ttThumbLoadInit === '1') return;
    root.dataset.ttThumbLoadInit = '1';

    var imgs = root.querySelectorAll('[data-tt-thumb-img]');
    if (!imgs.length) {
      root.classList.add('is-thumb-ready');
      return;
    }

    imgs.forEach(bindImageLoad);

    // First/priority image: native lazy is enough once near viewport.
    // Extra carousel frames stay deferred until navigated to (or prefetched for neighbours).
    var priority = root.querySelector('[data-tt-thumb-priority="1"]');
    if (priority && priority.complete && priority.naturalWidth > 0) {
      markImageLoaded(priority);
    }

    watchLoaderMotion(root);

    if (!('IntersectionObserver' in window)) {
      ensureSlideImage(root, 0);
      return;
    }

    if (!thumbObserver) {
      thumbObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var media = entry.target;
            ensureSlideImage(media, 0);
            // Warm the next slide only when the card is on-screen.
            ensureSlideImage(media, 1);
            thumbObserver.unobserve(media);
          });
        },
        { rootMargin: '180px 0px', threshold: 0.01 }
      );
    }

    thumbObserver.observe(root);
  }

  function initCarousel(root) {
    if (!root || root.dataset.ttThumbInit) return;
    root.dataset.ttThumbInit = 'true';

    initThumbLoading(root);

    var track = root.querySelector('[data-tt-thumb-track]');
    var slides = root.querySelectorAll('[data-tt-thumb-slide]');
    var prevBtn = root.querySelector('[data-tt-thumb-prev]');
    var nextBtn = root.querySelector('[data-tt-thumb-next]');
    var orbsRoot = root.querySelector('[data-tt-thumb-orbs]');
    var total = slides.length;

    if (total < 2 || !track || !prevBtn || !nextBtn || !orbsRoot) return;

    var current = 0;
    var orbSlots = [];
    var prevWindowStart = 0;

    for (var i = 0; i < MAX_ORBS; i++) {
      var orb = document.createElement('span');
      orb.className = 'tt-tile-card__orb';
      orb.setAttribute('role', 'presentation');
      orbsRoot.appendChild(orb);
      orbSlots.push(orb);
    }

    function renderOrbs() {
      var windowInfo = getOrbWindow(current, total);
      var windowChanged = windowInfo.start !== prevWindowStart;
      prevWindowStart = windowInfo.start;

      orbSlots.forEach(function (orb, slot) {
        var imageIndex = windowInfo.start + slot;

        if (slot >= windowInfo.count) {
          orb.hidden = true;
          orb.classList.remove('is-active', 'is-entering', 'is-leaving');
          return;
        }

        var prevIndex = orb.dataset.index ? parseInt(orb.dataset.index, 10) : -1;
        orb.hidden = false;
        orb.dataset.index = String(imageIndex);

        orb.classList.toggle('is-active', imageIndex === current);
        orb.classList.remove('is-entering', 'is-leaving');

        if (windowChanged && prevIndex !== imageIndex) {
          if (prevIndex === -1 || prevIndex < windowInfo.start || prevIndex >= windowInfo.start + windowInfo.count) {
            orb.classList.add('is-entering');
          } else if (imageIndex < prevIndex) {
            orb.classList.add('is-entering');
          } else {
            orb.classList.add('is-entering');
          }
        }
      });
    }

    function goTo(index) {
      current = (index + total) % total;
      track.style.setProperty('--tt-thumb-index', String(current));
      ensureSlideImage(root, current);
      ensureSlideImage(root, (current + 1) % total);
      renderOrbs();
    }

    function getCurrent() {
      return current;
    }

    initTouchSwipe(root, goTo, getCurrent);

    prevBtn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      goTo(current - 1);
    });

    nextBtn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      goTo(current + 1);
    });

    renderOrbs();
  }

  function initTouchSwipe(root, goTo, getCurrent) {
    var viewport = root.querySelector('.tt-tile-card__viewport');
    var card = root.closest('.tt-tile-card');
    var productLink = card && card.querySelector('.tt-tile-card__stretched-link');
    if (!viewport) return;

    var startX = 0;
    var startY = 0;
    var tracking = false;
    var isHorizontal = false;
    var didSwipe = false;
    var swipeThreshold = 36;
    var lockRatio = 1.25;

    viewport.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      tracking = true;
      isHorizontal = false;
      didSwipe = false;
    }, { passive: true });

    viewport.addEventListener('touchmove', function (event) {
      if (!tracking || event.touches.length !== 1) return;

      var dx = event.touches[0].clientX - startX;
      var dy = event.touches[0].clientY - startY;

      if (!isHorizontal) {
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * lockRatio) {
          isHorizontal = true;
        } else if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx) * lockRatio) {
          tracking = false;
          return;
        }
      }

      if (isHorizontal) {
        event.preventDefault();
      }
    }, { passive: false });

    viewport.addEventListener('touchend', function (event) {
      if (!tracking) return;
      tracking = false;

      var touch = event.changedTouches[0];
      var dx = touch.clientX - startX;
      var dy = touch.clientY - startY;

      if (isHorizontal && Math.abs(dx) >= swipeThreshold && Math.abs(dx) > Math.abs(dy) * lockRatio) {
        didSwipe = true;
        goTo(dx < 0 ? getCurrent() + 1 : getCurrent() - 1);
        window.setTimeout(function () {
          didSwipe = false;
        }, 450);
      }
    }, { passive: true });

    viewport.addEventListener('click', function (event) {
      if (didSwipe) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.target.closest('[data-tt-thumb-prev], [data-tt-thumb-next], [data-tt-favourite-toggle]')) return;

      if (productLink) {
        event.preventDefault();
        productLink.click();
      }
    });
  }

  function initMediaLink(card) {
    var media = card.querySelector('.tt-tile-card__media');
    var link = card.querySelector('.tt-tile-card__stretched-link');
    if (!media || !link || media.dataset.ttMediaLinkInit) return;

    var carousel = card.querySelector('[data-tt-thumb-carousel]');
    var imageCount = carousel ? parseInt(carousel.dataset.ttThumbCount || '0', 10) : 0;
    if (imageCount > 1) return;

    media.dataset.ttMediaLinkInit = 'true';
    media.addEventListener('click', function (event) {
      if (event.target.closest('.tt-tile-card__nav, [data-tt-favourite-toggle]')) return;
      event.preventDefault();
      link.click();
    });
  }

  function initAll() {
    document.querySelectorAll('[data-tt-thumb-carousel]').forEach(initCarousel);
    document.querySelectorAll('.tt-tile-card').forEach(initMediaLink);
  }

  window.TTTileCard = { initAll: initAll };

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('tt:tile-cards-updated', initAll);
})();
